import { PartnershipConfig } from '@/types/partnership.types';

export const ashokaPartnership: PartnershipConfig = {
  id: 'ashoka',
  
  partner: {
    name: 'Ashoka',
    logo: '/uploads/Ashoka_idqSyJERtL_0 copy.png',
    colors: {
      primary: '#003F72',
      secondary: '#E98300',
    },
  },
  
  slides: {
    title: {
      enabled: true,
      title: 'Building the Future of<br />Impact Funding',
      subtitle: 'Removing friction and creating a new OS for Systems Impact',
    },
    
    opportunity: {
      enabled: true,
      challenges: {
        title: '❌ Traditional Challenges',
        items: [
          { icon: '⏱️', text: '270+ day approval cycles' },
          { icon: '📉', text: 'Limited real-time insights' },
          { icon: '📄', text: '40+ page applications required' },
          { icon: '🏆', text: 'Competition for funding' },
          { icon: '🚧', text: 'Massive friction in the process' },
        ],
      },
      model: {
        title: '✨ Changemakers Model',
        items: [
          { icon: '🎯', text: 'New model for systems change' },
          { icon: '🔍', text: 'Capability-based assessment' },
          { icon: '🤝', text: 'Democratic fund allocation' },
          { icon: '🌍', text: 'Collaborative partnerships' },
          { icon: '⚡', text: 'Technology-enabled efficiency' },
        ],
      },
    },
    
    alignment: {
      enabled: true,
      allegoryMission: 'Accelerate capital allocation for world-changing innovations',
      partnerVision: 'Democratic, capability-based, collaborative funding model',
      sharedPrinciples: [
        { icon: '🏛️', title: 'Shared Governance & Accountability' },
        { icon: '📊', title: 'Fund Allocation by Demonstrated Capabilities' },
        { icon: '📚', title: 'Optimize for Collective Learning' },
      ],
    },
    
    fundingOS: {
      enabled: true,
      features: [
        { 
          title: 'Scout AI',
          isScout: true,
          desc: 'Intelligent proposal creation from existing documents, reducing application burden from weeks to hours' 
        },
        { 
          icon: '🎯',
          title: 'AI-Powered Matching',
          desc: 'Smart algorithms connecting applicants with the right funders, Fellows, and collaborative partners' 
        },
        { 
          icon: '👁️',
          title: 'Real-Time Transparency',
          desc: 'Live evidence tracking, progress monitoring, and impact measurement for all stakeholders' 
        },
        { 
          icon: '❤️',
          title: 'Collaboration by Design',
          desc: 'Community at the heart of the model, learning and sharing is incentivized' 
        },
      ],
    },
    
    partnerModel: {
      enabled: true,
      title: "Ashoka's Systems Impact Model",
      subtitle: 'The Changemaker Impact Capabilities Framework',
      usePartnerColors: true,  // Apply Ashoka's brand colors to this slide
      capabilities: [
        'Learning, improvement, & results orientation',
        'Leadership & culture',
        'Capacity strengthening & protection',
        'Systems change',
        'Accountability, monitoring, & reporting',
        'Equitable agreement processes & operations',
        'Financial support & systems',
      ],
      dataStreams: [
        { icon: '💵', label: 'Spend' },
        { icon: '⚡', label: 'Actions' },
        { icon: '🎯', label: 'Direct Outcomes' },
        { icon: '🏆', label: 'Capabilities' },
        { icon: '🌍', label: 'State of Problem' },
      ],
    },
    
    phase1: {
      enabled: true,
      title: 'Phase 1: Design Partnership',
      dateRange: 'November 2025 - February 2026',
      deliverables: [
        {
          title: 'Capabilities Assessment Tool',
          features: [
            'Configurable assessment framework',
            'Risk scoring algorithm',
            'Normalization layer',
            'Metadata tagging system',
          ],
        },
        {
          title: 'AI-Powered Matching Engine',
          features: [
            'Matching algorithm',
            'Recommendation system',
            'Scoring dashboard',
            'Community collaboration',
          ],
        },
        {
          title: 'Project & Reporting Design Partnership',
          features: [
            'Shared Projects object',
            'Sub-project allocation',
            'Fund allocation workflow',
          ],
        },
      ],
      metrics: {
       timeline: '10 Weeks',
        successMetric: 'Working Demo for February investment meeting',
        //: '$50,000',
      },
    },
    
    timeline: {
      enabled: false,
      title: 'Phase 1: Timeline & Milestones',
      subtitle: '12-week sprint to February investment meeting',
      items: [
        {
          week: 'Week of Nov 18',
          action: 'Present Roadmap',
          detail: 'Share proposal with Ashoka leadership for feedback',
        },
        {
          week: 'Week of Nov 25',
          action: 'Refine Scope',
          detail: 'Finalize Phase 1 contract terms and deliverables',
        },
        {
          week: 'Week of Dec 2',
          action: 'Execute Contract',
          detail: 'Sign agreement and begin development sprint',
        },
        {
          week: 'Dec 2 - Feb 14',
          action: 'Development Sprint',
          detail: 'Weekly check-ins, bi-weekly demos, collaborative product development',
        },
        {
          week: 'February 14, 2026',
          action: '🎯 Deliver Prototype',
          detail: 'Present working platform at investment meeting',
        },
      ],
    },
    
    phase2: {
      enabled: true,
      title: 'Phase 2: Long-Term Partnership',
      subtitle: 'Five-year strategic partnership (2026-2030)',
      years: [
        {
          label: 'Year 1',
          title: 'Foundation & Launch',
          activities: [
            'Design partnership buildout',
            'First wave deployment',
            'Platform launch and capacity building',
            'Transaction transparency activated',
          ],
        },
        {
          label: 'Years 2-3',
          title: 'Expansion & Growth',
          activities: [
            'Scale to $20M fund',
            'Advanced collaboration',
            'AI learning pathways',
            'Open marketplace',
          ],
        },
        {
          label: 'Years 4-5',
          title: 'Platform Maturity',
          activities: [
            'Industry standard',
            'White-label licensing',
            'AI co-pilot launch',
            'Open API marketplace',
          ],
        },
      ],
      showTotalValue: false,
    },
    
    benefitsPartner: {
      enabled: true,
      title: 'Strategic Benefits for Ashoka',
      subtitle: 'Why this partnership makes sense',
      usePartnerColors: true,
      benefits: [
        {
          icon: '🛡️',
          title: 'Risk Mitigation',
          desc: 'Phased approach with February pilot demonstrates capability before full commitment. Low-risk path to innovation.',
        },
        {
          icon: '⚡',
          title: 'Speed to Market',
          desc: "Leverage Allegory's existing Funding OS infrastructure rather than building from scratch. Launch in months, not years.",
        },
        {
          icon: '🌐',
          title: 'New Model for Systems Change',
          desc: 'Platform designed for openness and collaboration. Your data, your community, your control.',
        },
        {
          icon: '🚀',
          title: 'Innovation Partnership',
          desc: 'Access to cutting-edge AI, real-time transparency, and modern UX that sets new standards for impact funding.',
        },
        {
          icon: '💰',
          title: 'Cost Efficiency',
          desc: 'SaaS model more sustainable than custom development and maintenance. Predictable costs, continuous improvements.',
        },
      ],
    },
    
    benefitsAllegory: {
      enabled: true,
      title: 'Strategic Benefits for Allegory',
      subtitle: 'Mutual value creation',
      benefits: [
        {
          icon: '✅',
          title: 'Validation',
          desc: 'Partnership with Ashoka establishes credibility in impact funding sector and validates our technology approach.',
        },
        {
          icon: '🎯',
          title: 'Strategic Focus',
          desc: 'Aligns perfectly with Funding OS vision—removing friction from grant funding at scale.',
        },
        {
          icon: '🔧',
          title: 'Product Development',
          desc: "Ashoka's requirements push platform capabilities forward for all customers.",
        },
        {
          icon: '🏆',
          title: 'Leadership Position',
          desc: 'Reference customer to other foundations and impact funds.',
        },
        {
          icon: '❤️',
          title: 'Mission Alignment',
          desc: 'Direct path to realizing vision of connecting capital, people, and organizations for systemic change.',
        },
      ],
    },
    
    contract: {
      enabled: true,
      phase1: {
        type: 'Fixed-price development agreement',
        duration: 'November 2025 - February 2026',
        payment: '50% upfront, 50% on delivery',
        success: 'Working prototype at February meeting',
      },
      phase2: {
        type: '5-year Master Services Agreement',
        duration: 'March 2026 - February 2031',
        exclusivity: 'Non-exclusive partnership',
      },
      showPricing: false,
    },
    
    whyWorks: {
      enabled: true,
      partnerBrings: [
        'Global network of Fellows and changemakers',
        'Proven funding model expertise',
        'Deep mission alignment',
        'Validation and credibility',
        'Systems change methodology',
      ],
      allegoryBrings: [
        'Cutting-edge technology platform',
        'Speed to market execution',
        'AI and automation capabilities',
        'Scalable infrastructure',
        'Collaboration at the heart',
      ],
      togetherStatement: 'We can usher a new era of Systems Impact and accellerate Changemakers to solve our biggest problems',
    },
    
    cta: {
      enabled: true,
      title: "Let's Create a new OS for Systems Impact",
      nextSteps: [
        {
          icon: '📅',
          step: 'Week of Nov 18',
          desc: 'Present this roadmap to Ashoka leadership',
        },
        {
          icon: '🔧',
          step: 'Week of Nov 25',
          desc: 'Refine scope and finalize contract terms',
        },
        {
          icon: '✍️',
          step: 'Week of Dec 2',
          desc: 'Execute Phase 1 contract',
        },
        {
          icon: '🚀',
          step: 'Dec 2 - Feb 14',
          desc: 'Build together',
        },
        {
          icon: '🎯',
          step: 'February 2026',
          desc: 'Launch at investment meeting',
        },
        {
          icon: '🌍',
          step: '2026-2030',
          desc: 'Scale the model globally',
        },
      ],
      closingStatement: "A partnership unlocking impact to solve the world's most pressing problems. Let's build the future of impact funding together.",
    },
  },

  // Optional: Custom slide order (if not specified, uses default order)
  // Uncomment and modify to rearrange slides:
  slideOrder: [
    'title',
    'opportunity',
    'fundingOS',
    'partnerModel',
    'alignment',
    'benefitsPartner',
    'benefitsAllegory',
    'phase1',
    'timeline',
    'phase2',
    'whyWorks',
    'cta',
  ],
};

